import React from "react";
import Swal from "sweetalert2";

function moneyBRL(v) {
    return Number(v || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function maskPhoneBR(value) {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 10) {
        return digits
            .replace(/^(\d{2})(\d)/g, "($1) $2")
            .replace(/(\d{4})(\d)/, "$1-$2");
    }

    return digits
        .replace(/^(\d{2})(\d)/g, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
}

function getItemProductType(item) {
    return item?.productType || item?.chainType || "-";
}

export default function CartSidebar({
    items,
    customer,
    setCustomer,
    total,
    onRemoveItem,
    onGenerateOrder,
    seller,
}) {
    const customerName = String(customer?.name || "").trim();
    const phoneDigits = String(customer?.phone || "").replace(/\D/g, "");

    const hasItems = items?.length > 0;
    const hasName = customerName.length > 0;
    const hasPhone = phoneDigits.length > 0;
    const hasValidPhone = phoneDigits.length >= 10;

    const isReadyToGenerate = hasItems && hasName && hasValidPhone;

    function handleCheckoutClick() {
        if (!hasItems) {
            Swal.fire({
                icon: "warning",
                title: "Seu carrinho está vazio",
                text: "Adicione pelo menos um item antes de gerar o pedido.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (!hasName && !hasPhone) {
            Swal.fire({
                icon: "warning",
                title: "Preencha seus dados",
                html: `
                    <div style="text-align:left;">
                        <div>• Nome é obrigatório</div>
                        <div>• WhatsApp é obrigatório</div>
                    </div>
                `,
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (!hasName) {
            Swal.fire({
                icon: "warning",
                title: "Nome obrigatório",
                text: "Preencha seu nome para continuar.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (!hasPhone) {
            Swal.fire({
                icon: "warning",
                title: "WhatsApp obrigatório",
                text: "Preencha seu WhatsApp para continuar.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        if (!hasValidPhone) {
            Swal.fire({
                icon: "warning",
                title: "WhatsApp inválido",
                text: "Digite um número de WhatsApp válido com DDD.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        onGenerateOrder();
    }

    return (
        <div style={styles.sidebar}>
            <div style={styles.sellerBox}>
                <div style={styles.sellerLabel}>Atendimento</div>
                <div style={styles.sellerName}>{seller?.display_name || "-"}</div>
            </div>

            <div style={styles.customerBlock}>
                <div style={styles.blockTitle}>Seus dados</div>

                <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Nome</label>
                    <input
                        style={styles.input}
                        placeholder="Digite seu nome"
                        value={customer.name}
                        onChange={(e) =>
                            setCustomer((prev) => ({ ...prev, name: e.target.value }))
                        }
                    />
                </div>

                <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>WhatsApp</label>
                    <input
                        style={styles.input}
                        placeholder="(00) 00000-0000"
                        value={customer.phone}
                        onChange={(e) => {
                            const masked = maskPhoneBR(e.target.value);

                            setCustomer((prev) => ({
                                ...prev,
                                phone: masked,
                            }));
                        }}
                    />
                </div>
            </div>

            <div style={styles.itemsBlock}>
                <div style={styles.blockTitle}>Itens ({items?.length})</div>

                {!items.length ? (
                    <div style={styles.empty}>Nenhum item adicionado ainda.</div>
                ) : (
                    <div style={styles.itemsList}>
                        {items.map((item, index) => (
                            <div key={`${item.optionId}-${index}`} style={styles.itemCard}>
                                <div style={styles.itemTop}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={styles.itemName}>{item.productName}</div>

                                        {!!item.customText && (
                                            <div style={styles.itemCustom}>
                                                {item.customText}
                                            </div>
                                        )}

                                        <div style={styles.itemMeta}>
                                            {getItemProductType(item)} • {item.color || "-"}
                                        </div>

                                        <div style={styles.itemMetaLight}>
                                            {item.code || "-"} • Qtd {item.quantity}
                                        </div>
                                    </div>

                                    <div style={styles.itemRight}>
                                        <div style={styles.itemPrice}>
                                            {moneyBRL(item.price * item.quantity)}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => onRemoveItem(index)}
                                            style={styles.removeBtn}
                                        >
                                            Remover
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={styles.footer}>
                <div>
                    <div style={styles.totalLabel}>Total Pedido</div>
                    <div style={styles.totalValue}>{moneyBRL(total)}</div>
                </div>

                <button
                    type="button"
                    onClick={handleCheckoutClick}
                    aria-disabled={!isReadyToGenerate}
                    style={{
                        ...styles.checkoutBtn,
                        ...(!isReadyToGenerate ? styles.checkoutBtnDisabled : {}),
                    }}
                >
                    Gerar pedido
                </button>
            </div>
        </div>
    );
}

const styles = {
    sidebar: {
        background: "#fff",
        padding: 16,
        display: "grid",
        gap: 16,
        height: "100%",
        overflowY: "auto",
        boxSizing: "border-box",
        gridTemplateRows: "auto auto 1fr auto",
    },

    sellerBox: {
        paddingBottom: 12,
        borderBottom: "1px solid rgba(17,24,39,0.08)",
    },

    sellerLabel: {
        fontSize: 11,
        fontWeight: 800,
        color: "#902348",
        textTransform: "uppercase",
        letterSpacing: 0.6,
    },

    sellerName: {
        marginTop: 4,
        fontSize: 16,
        fontWeight: 900,
        color: "#111827",
    },

    customerBlock: {
        display: "grid",
        gap: 10,
    },

    blockTitle: {
        fontSize: 14,
        fontWeight: 900,
        color: "#111827",
    },

    inputGroup: {
        display: "grid",
        gap: 5,
    },

    inputLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#6b7280",
    },

    input: {
        width: "100%",
        border: "none",
        borderBottom: "1px solid rgba(17,24,39,0.14)",
        padding: "8px 2px",
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        background: "transparent",
        outline: "none",
        boxSizing: "border-box",
    },

    empty: {
        padding: 10,
        borderRadius: 10,
        background: "rgba(17,24,39,0.03)",
        color: "#6b7280",
        fontWeight: 600,
        fontSize: 13,
    },

    itemsBlock: {
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr)",
        gap: 8,
        minHeight: 0,
    },

    itemsList: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        overflowY: "auto",
        minHeight: 0,
        justifyContent: "flex-start",
    },

    itemCard: {
        minHeight: 88,
        padding: 10,
        borderRadius: 12,
        border: "1px solid rgba(17,24,39,0.06)",
        background: "#fafafa",
        boxSizing: "border-box",
    },

    itemTop: {
        display: "flex",
        gap: 10,
    },

    itemName: {
        fontSize: 13,
        fontWeight: 800,
        color: "#111827",
        lineHeight: 1.3,
    },

    itemCustom: {
        marginTop: 4,
        fontSize: 12,
        color: "#902348",
        fontWeight: 700,
    },

    itemMeta: {
        marginTop: 4,
        fontSize: 12,
        color: "#4b5563",
        fontWeight: 600,
    },

    itemMetaLight: {
        marginTop: 3,
        fontSize: 11,
        color: "#8b95a1",
        fontWeight: 600,
    },

    itemRight: {
        textAlign: "right",
        minWidth: 90,
    },

    itemPrice: {
        fontWeight: 900,
        fontSize: 13,
        color: "#111827",
    },

    removeBtn: {
        marginTop: 8,
        border: "none",
        background: "transparent",
        padding: 0,
        fontSize: 12,
        fontWeight: 700,
        color: "#902348",
        cursor: "pointer",
    },

    footer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        paddingTop: 14,
        borderTop: "1px solid rgba(17,24,39,0.08)",
        marginTop: 4,
    },

    totalLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#6b7280",
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },

    totalValue: {
        fontSize: 22,
        fontWeight: 900,
        color: "#111827",
        marginTop: 2,
    },

    checkoutBtn: {
        border: "none",
        borderRadius: 10,
        padding: "11px 14px",
        background: "#902348",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 180ms ease",
    },

    checkoutBtnDisabled: {
        background: "#9ca3af",
        color: "#ffffff",
        cursor: "pointer",
        opacity: 1,
    },
};